/**
 * Contractor Detail Page
 * Shows a contractor's full profile. Homeowners/designers with active projects
 * can invite the contractor directly from this page.
 */

import type { TargetReviewsResponse } from "@shared/types";
import {
	ArrowLeft,
	BadgeCheck,
	Clock,
	Hammer,
	ImageIcon,
	MapPin,
	Send,
	Users,
	X,
} from "lucide-react";
import type * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardTitle,
} from "@/components/ui/card";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { ReviewSummarySection } from "@/components/ui/review-summary-section";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/features/auth/store/auth.store";
import {
	type ContractorProfile,
	getContractorById,
} from "@/features/marketplace/services/marketplace-api.service";
import {
	getProjectContractors,
	inviteContractor,
	listMyProjects,
	type Project,
} from "@/features/projects/services/projects-api.service";
import { formatPrice } from "@/lib/format";
import { cn, resolveMediaUrl } from "@/lib/utils";
import { analytics } from "@/services/analytics.service";
import { fetchContractorReviews } from "@/services/reviews-api.service";

// ============================================================================
// LOADING SKELETON
// ============================================================================

function ContractorDetailSkeleton(): React.ReactElement {
	return (
		<div className="min-h-screen bg-muted">
			<div className="bg-card border-b">
				<div className="mx-auto max-w-4xl px-4 py-4">
					<Skeleton className="h-10 w-24" />
				</div>
			</div>
			<div className="mx-auto max-w-4xl px-4 py-6 space-y-6">
				<Card className="p-6">
					<div className="flex items-start gap-4">
						<Skeleton className="h-20 w-20 rounded-full" />
						<div className="flex-1 space-y-2">
							<Skeleton className="h-7 w-48" />
							<Skeleton className="h-4 w-full" />
							<Skeleton className="h-4 w-3/4" />
						</div>
					</div>
				</Card>
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<Skeleton className="h-24 rounded-xl" />
					<Skeleton className="h-24 rounded-xl" />
				</div>
				<Card className="p-6 space-y-4">
					<Skeleton className="h-6 w-40" />
					<div className="flex flex-wrap gap-2">
						<Skeleton className="h-8 w-24 rounded-full" />
						<Skeleton className="h-8 w-20 rounded-full" />
						<Skeleton className="h-8 w-28 rounded-full" />
					</div>
				</Card>
				<Card className="p-6 space-y-4">
					<Skeleton className="h-6 w-32" />
					<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
						<Skeleton className="aspect-square rounded-lg" />
						<Skeleton className="aspect-square rounded-lg" />
						<Skeleton className="aspect-square rounded-lg" />
					</div>
				</Card>
			</div>
		</div>
	);
}

// ============================================================================
// NOT FOUND STATE
// ============================================================================

function ContractorNotFound(): React.ReactElement {
	const { t } = useTranslation();
	const navigate = useNavigate();

	return (
		<div className="min-h-screen bg-muted">
			<div className="bg-card border-b">
				<div className="mx-auto max-w-4xl px-4 py-4">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => navigate("/contractors")}
					>
						<ArrowLeft className="h-4 w-4" />
						{t("contractorDetail.backToContractors")}
					</Button>
				</div>
			</div>
			<div className="mx-auto max-w-4xl px-4 py-12">
				<Card className="p-12 text-center">
					<CardContent className="space-y-4 p-0">
						<div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-muted">
							<Hammer className="h-8 w-8 text-muted-foreground" />
						</div>
						<CardTitle className="text-xl">
							{t("contractorDetail.contractorNotFound")}
						</CardTitle>
						<CardDescription className="text-base">
							{t("contractorDetail.contractorNotFoundDesc")}
						</CardDescription>
						<Button variant="primary" onClick={() => navigate("/contractors")}>
							{t("contractorDetail.browseContractors")}
						</Button>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}

// ============================================================================
// INVITE DIALOG
// ============================================================================

/**
 * Whether a contractor is on a project: invited, working on it or done with
 * it. A contractor who was removed or declined can be invited again, so those
 * rows do not count. `getProjectContractors` answers an empty list when its
 * request fails, so a failed read cannot mark anyone as invited.
 */
async function isOnProject(
	projectId: string,
	contractorId: string,
): Promise<boolean> {
	const { contractors } = await getProjectContractors(projectId);
	return contractors.some(
		(c) =>
			c.contractorId === contractorId &&
			c.status !== "removed" &&
			c.status !== "declined",
	);
}

interface InviteDialogProps {
	contractor: ContractorProfile;
	onClose: () => void;
	onSuccess: () => void;
}

function InviteDialog({
	contractor,
	onClose,
	onSuccess,
}: InviteDialogProps): React.ReactElement {
	const { t } = useTranslation();
	const [projects, setProjects] = useState<Project[]>([]);
	const [selectedProjectId, setSelectedProjectId] = useState<string>("");
	const [isLoadingProjects, setIsLoadingProjects] = useState(true);
	const [projectsFailed, setProjectsFailed] = useState(false);
	const [isSending, setIsSending] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [invitedProjectIds, setInvitedProjectIds] = useState<
		ReadonlySet<string>
	>(new Set());

	useEffect(() => {
		async function fetchProjects(): Promise<void> {
			setIsLoadingProjects(true);
			try {
				const allProjects = await listMyProjects();
				const eligibleProjects = allProjects.filter(
					(p) => p.status !== "completed" && p.status !== "cancelled",
				);
				const alreadyOn = await Promise.all(
					eligibleProjects.map(async (p) =>
						(await isOnProject(p.id, contractor.id)) ? p.id : null,
					),
				);
				const invited = new Set(alreadyOn.filter((id) => id !== null));
				setInvitedProjectIds(invited);
				setProjects(eligibleProjects);
				const firstOpen = eligibleProjects.find((p) => !invited.has(p.id));
				if (firstOpen) {
					setSelectedProjectId(firstOpen.id);
				}
			} catch {
				setProjectsFailed(true);
				setError(t("contractorDetail.failedToLoadProjects"));
			} finally {
				setIsLoadingProjects(false);
			}
		}
		fetchProjects();
	}, [t, contractor.id]);

	/** After a refused invite, learn whether it was because the contractor is already on the project. */
	async function markIfInvited(projectId: string): Promise<void> {
		if (await isOnProject(projectId, contractor.id)) {
			setInvitedProjectIds((current) => new Set(current).add(projectId));
		}
	}

	async function handleInvite(): Promise<void> {
		if (!selectedProjectId || invitedProjectIds.has(selectedProjectId)) {
			setError(t("contractorDetail.pleaseSelectProject"));
			return;
		}

		setIsSending(true);
		setError(null);

		try {
			const result = await inviteContractor(selectedProjectId, contractor.id);

			if (result.success) {
				onSuccess();
			} else {
				setError(result.error || t("contractorDetail.failedToInvite"));
				await markIfInvited(selectedProjectId);
			}
		} catch {
			setError(t("contractorDetail.failedToInvite"));
		} finally {
			setIsSending(false);
		}
	}

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs">
			<div className="w-full max-w-md rounded-2xl border border-dashboard-border bg-dashboard-paper p-6 shadow-[var(--dashboard-shadow-4)]">
				<div className="flex items-center justify-between mb-2">
					<h2 className="font-display text-[20px] font-medium text-dashboard-text">
						{t("contractorDetail.inviteToProject")}
					</h2>
					<Button
						variant="ghost"
						size="icon"
						onClick={onClose}
						className="rounded-full hover:bg-dashboard-surface"
					>
						<X className="h-5 w-5 text-dashboard-muted" />
					</Button>
				</div>
				<p className="font-body text-xs text-dashboard-muted mb-5 leading-relaxed">
					{t("contractorDetail.inviteToProjectDesc", {
						name: contractor.name,
					})}
				</p>

				<div className="space-y-4 mb-6">
					<div className="space-y-2">
						{isLoadingProjects ? (
							<Skeleton className="h-10 w-full rounded-xl" />
						) : projectsFailed ? null : projects.length === 0 ? (
							<div className="rounded-xl border border-dashed border-dashboard-border p-4 text-center">
								<p className="font-body text-sm text-dashboard-muted">
									{t("contractorDetail.noActiveProjects")}
								</p>
							</div>
						) : (
							<div className="space-y-2 max-h-52 overflow-y-auto pr-1">
								{projects.map((project) => (
									<button
										key={project.id}
										type="button"
										className={cn(
											"w-full text-left cursor-pointer rounded-xl border p-3.5 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60",
											selectedProjectId === project.id
												? "border-dashboard-accent bg-dashboard-surface/50 shadow-[var(--dashboard-shadow-1)]"
												: "border-dashboard-border hover:bg-dashboard-surface/30",
										)}
										disabled={invitedProjectIds.has(project.id)}
										onClick={() => setSelectedProjectId(project.id)}
									>
										<div className="flex items-center gap-3">
											<div
												className={cn(
													"h-4 w-4 shrink-0 rounded-full border-2 transition-colors",
													selectedProjectId === project.id
														? "border-dashboard-accent bg-dashboard-accent"
														: "border-dashboard-muted",
												)}
											/>
											<div className="min-w-0">
												<p className="font-display text-sm font-medium text-dashboard-text truncate">
													{project.title}
												</p>
												<p className="font-mono text-[9px] uppercase tracking-wider text-dashboard-muted mt-0.5">
													{project.scope === "full_home"
														? t("project.fullHome")
														: t("project.partial")}
													{invitedProjectIds.has(project.id) &&
														` · ${t("contractorDetail.alreadyInvited")}`}
												</p>
											</div>
										</div>
									</button>
								))}
							</div>
						)}
					</div>

					{error && (
						<div className="rounded-xl border border-destructive/20 bg-destructive/10 p-3">
							<p className="text-sm text-destructive font-body">{error}</p>
						</div>
					)}
				</div>

				<div className="flex gap-3">
					<Button
						variant="outline"
						onClick={onClose}
						className="flex-1 rounded-xl border border-dashboard-border bg-dashboard-paper hover:bg-dashboard-surface font-mono text-[10px] uppercase font-bold tracking-wider text-dashboard-text h-10 transition-colors shadow-[var(--dashboard-shadow-1)]"
					>
						{t("common.cancel")}
					</Button>
					<Button
						variant="primary"
						onClick={handleInvite}
						isLoading={isSending}
						disabled={
							projects.length === 0 ||
							!selectedProjectId ||
							invitedProjectIds.has(selectedProjectId)
						}
						className="flex-1 rounded-xl border-none bg-dashboard-accent hover:bg-dashboard-accent-hover font-mono text-[10px] uppercase font-bold tracking-wider text-dashboard-on-fill h-10 transition-all shadow-[var(--dashboard-shadow-1)]"
					>
						<Send className="h-4 w-4" />
						{t("contractorDetail.invite")}
					</Button>
				</div>
			</div>
		</div>
	);
}

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

export function ContractorDetailPage(): React.ReactElement {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { user } = useAuthStore();

	const [contractor, setContractor] = useState<ContractorProfile | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [notFound, setNotFound] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [showInviteDialog, setShowInviteDialog] = useState(false);
	const [inviteSent, setInviteSent] = useState(false);
	const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
	const [reviewsFailed, setReviewsFailed] = useState(false);
	const [reviews, setReviews] = useState<TargetReviewsResponse>({
		summary: { averageRating: null, reviewCount: 0 },
		reviews: [],
	});

	const canInvite =
		user?.userType === "homeowner" || user?.userType === "designer";

	const fetchContractor = useCallback(async () => {
		if (!id) {
			setNotFound(true);
			setIsLoading(false);
			return;
		}

		setIsLoading(true);
		setError(null);
		setNotFound(false);

		try {
			const result = await getContractorById(id);
			if (result) {
				setContractor(result);
				analytics.contractor.profileViewed({ contractorId: id });
			} else {
				setNotFound(true);
			}
		} catch (err) {
			console.error("Failed to fetch contractor details:", err);
			setError(t("common.loadError", "Failed to load contractor details"));
		} finally {
			setIsLoading(false);
		}
	}, [id, t]);

	const fetchReviews = useCallback(async () => {
		if (!id) return;
		try {
			const reviewResult = await fetchContractorReviews(id);
			setReviews(reviewResult);
			setReviewsFailed(false);
		} catch (err) {
			console.error("Failed to fetch contractor reviews:", err);
			setReviewsFailed(true);
		}
	}, [id]);

	useEffect(() => {
		void fetchContractor();
	}, [fetchContractor]);

	useEffect(() => {
		if (contractor) {
			void fetchReviews();
		}
	}, [contractor, fetchReviews]);

	if (error) {
		return (
			<div className="min-h-screen bg-muted">
				<div className="bg-card border-b">
					<div className="mx-auto max-w-4xl px-4 py-4">
						<Button
							variant="ghost"
							size="sm"
							onClick={() => navigate("/contractors")}
							className="font-mono text-[10px] uppercase font-bold tracking-wider text-dashboard-text hover:bg-dashboard-surface/65"
						>
							<ArrowLeft className="h-4 w-4 text-dashboard-accent-dark" />
							{t("contractorDetail.backToContractors")}
						</Button>
					</div>
				</div>
				<div className="mx-auto max-w-4xl px-4 py-12">
					<Card className="p-12 text-center border-destructive/20 bg-destructive/5">
						<CardContent className="space-y-4 p-0 flex flex-col items-center">
							<div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-dashboard-pill-risk-bg text-dashboard-pill-risk mb-2">
								<svg
									className="h-6 w-6"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<title>Error</title>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
									/>
								</svg>
							</div>
							<CardTitle className="text-xl">
								Failed to load contractor details
							</CardTitle>
							<CardDescription className="text-base text-dashboard-muted">
								{error}
							</CardDescription>
							<div className="flex justify-center gap-2 mt-4">
								<Button
									variant="primary"
									onClick={fetchContractor}
									className="bg-dashboard-accent hover:bg-dashboard-accent-hover border-none px-4"
								>
									Retry
								</Button>
								<Button
									variant="outline"
									onClick={() => navigate("/contractors")}
								>
									Browse Contractors
								</Button>
							</div>
						</CardContent>
					</Card>
				</div>
			</div>
		);
	}

	if (isLoading) return <ContractorDetailSkeleton />;
	if (notFound || !contractor) return <ContractorNotFound />;

	function handleInviteSuccess(): void {
		setShowInviteDialog(false);
		setInviteSent(true);
	}

	const location = [contractor.city, contractor.state]
		.filter(Boolean)
		.join(", ");
	const hasPriceRange =
		contractor.priceRangeMin != null || contractor.priceRangeMax != null;
	const projectTypes = contractor.projectTypes ?? [];
	const hasWorkDetails =
		hasPriceRange ||
		contractor.serviceRadiusKm != null ||
		projectTypes.length > 0 ||
		Boolean(contractor.projectTypesOther);
	const published = contractor.publishedProfile;
	const previousProjects = contractor.previousProjects ?? [];

	return (
		<div
			className="min-h-screen pb-12"
			style={{
				background:
					"radial-gradient(ellipse at 0% 0%, color-mix(in srgb, var(--color-dashboard-accent-light) 6%, transparent) 0%, transparent 45%), radial-gradient(ellipse at 100% 100%, color-mix(in srgb, var(--color-dashboard-accent) 8%, transparent) 0%, transparent 50%), var(--color-dashboard-surface)",
			}}
		>
			{/* Top Navigation Bar */}
			<div className="border-b border-dashboard-border/60 bg-transparent">
				<div className="mx-auto max-w-4xl px-4 py-4 flex items-center justify-between">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => navigate("/contractors")}
						className="font-mono text-[10px] uppercase font-bold tracking-wider text-dashboard-text hover:bg-dashboard-surface/65"
					>
						<ArrowLeft className="h-4 w-4 text-dashboard-accent-dark" />
						{t("contractorDetail.backToContractors")}
					</Button>

					{canInvite && (
						<Button
							variant="primary"
							size="sm"
							onClick={() => setShowInviteDialog(true)}
							className="h-9 rounded-xl border-none px-4 font-mono text-[10px] uppercase font-bold tracking-wider text-dashboard-on-fill bg-dashboard-accent hover:bg-dashboard-accent-hover transition-all duration-200 shadow-[var(--dashboard-shadow-1)]"
						>
							<Send className="h-4 w-4" />
							{t("contractorDetail.inviteToProject")}
						</Button>
					)}
				</div>
			</div>

			<div className="mx-auto max-w-4xl px-4 py-6 space-y-6">
				{/* Success banner */}
				{inviteSent && (
					<div className="rounded-2xl border border-dashboard-success-border bg-dashboard-success-bg/70 p-5 shadow-[var(--dashboard-shadow-1)] backdrop-blur-md">
						<div className="flex items-start gap-3.5">
							<Send className="h-5 w-5 text-dashboard-pill-done shrink-0 mt-0.5" />
							<div>
								<p className="font-display text-sm font-semibold text-dashboard-success-strong">
									{t("contractorDetail.inviteSentSuccess")}
								</p>
								<p className="font-body text-sm text-dashboard-success mt-1">
									{t("contractorDetail.inviteSentDesc", {
										name: contractor.name,
									})}
								</p>
							</div>
						</div>
					</div>
				)}

				{/* Profile Header Card */}
				<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper p-6 shadow-[var(--dashboard-shadow-2)]">
					<div className="flex flex-col sm:flex-row items-start gap-5">
						<div
							className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full text-2xl font-display font-bold border border-dashboard-border shadow-[var(--dashboard-shadow-avatar)]"
							style={{
								background: contractor.profilePictureUrl
									? "transparent"
									: "linear-gradient(135deg, var(--color-dashboard-warm-200), var(--color-dashboard-orange-300))",
								color: "var(--color-dashboard-orange-text-on-tint)",
							}}
						>
							{contractor.profilePictureUrl ? (
								<img
									src={contractor.profilePictureUrl}
									alt={contractor.name}
									className="h-20 w-20 rounded-full object-cover"
								/>
							) : (
								contractor.name.charAt(0).toUpperCase()
							)}
						</div>

						<div className="flex-1 min-w-0">
							<div className="flex items-center gap-2">
								<h1 className="font-display text-[28px] font-medium text-dashboard-text tracking-tight">
									{contractor.name}
								</h1>
								{contractor.isVerified && (
									<BadgeCheck className="h-6 w-6 text-dashboard-accent-dark shrink-0" />
								)}
							</div>

							{contractor.businessName &&
								contractor.businessName !== contractor.name && (
									<p className="mt-1 font-body text-[14px] text-dashboard-muted-dark">
										{contractor.businessName}
									</p>
								)}
							{location && (
								<p className="mt-1 flex items-center gap-1.5 font-body text-[13px] text-dashboard-muted">
									<MapPin className="h-3.5 w-3.5 text-dashboard-accent-dark" />
									{t("contractorDetail.basedIn", { place: location })}
								</p>
							)}

							{contractor.bio && (
								<p className="mt-3.5 font-body text-[14px] text-dashboard-muted-dark leading-relaxed">
									{contractor.bio}
								</p>
							)}
						</div>
					</div>
				</div>

				{/* Stats Row */}
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper p-5 shadow-[var(--dashboard-shadow-1)]">
						<div className="flex items-center gap-4">
							<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-dashboard-surface">
								<Clock className="h-5 w-5 text-dashboard-accent-dark" />
							</div>
							<div>
								<p className="font-mono text-[9px] font-semibold uppercase tracking-widest text-dashboard-muted">
									{t("contractorDetail.experience")}
								</p>
								<p className="font-display text-[20px] font-medium text-dashboard-text mt-0.5">
									{contractor.experienceYears != null
										? t("contractorDetail.xYears", {
												count: contractor.experienceYears,
											})
										: t("project.notSpecified")}
								</p>
							</div>
						</div>
					</div>

					<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper p-5 shadow-[var(--dashboard-shadow-1)]">
						<div className="flex items-center gap-4">
							<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-dashboard-surface">
								<Hammer className="h-5 w-5 text-dashboard-accent-dark" />
							</div>
							<div>
								<p className="font-mono text-[9px] font-semibold uppercase tracking-widest text-dashboard-muted">
									{t("contractorDetail.specializations")}
								</p>
								<p className="font-display text-[20px] font-medium text-dashboard-text mt-0.5">
									{t("contractorDetail.xTrades", {
										count: contractor.trades ? contractor.trades.length : 0,
									})}
								</p>
							</div>
						</div>
					</div>
				</div>

				<ReviewSummarySection
					title="Client Reviews"
					summary={reviews.summary}
					reviews={reviews.reviews}
					emptyMessage="No homeowner reviews yet."
					loadFailed={
						reviewsFailed
							? {
									message: t("reviews.loadFailed"),
									retryLabel: t("reviews.retry"),
									onRetry: () => void fetchReviews(),
								}
							: undefined
					}
				/>

				{/* Trades */}
				{contractor.trades && contractor.trades.length > 0 && (
					<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper p-6 shadow-[var(--dashboard-shadow-1)]">
						<h2 className="font-display text-[18px] font-medium text-dashboard-text border-b border-dashboard-divider pb-2.5 mb-4 flex items-center gap-2">
							<Hammer className="h-4.5 w-4.5 text-dashboard-accent-dark" />
							{t("contractorDetail.trades")}
						</h2>
						<div className="flex flex-wrap gap-2">
							{contractor.trades.map((trade) => (
								<span
									key={trade}
									className="inline-flex items-center rounded-full bg-dashboard-surface border border-dashboard-border px-3 py-1 font-body text-xs text-dashboard-accent-dark font-semibold"
								>
									{trade}
								</span>
							))}
						</div>
					</div>
				)}

				{/* Service Areas */}
				{contractor.serviceAreas && contractor.serviceAreas.length > 0 && (
					<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper p-6 shadow-[var(--dashboard-shadow-1)]">
						<h2 className="font-display text-[18px] font-medium text-dashboard-text border-b border-dashboard-divider pb-2.5 mb-4 flex items-center gap-2">
							<MapPin className="h-4.5 w-4.5 text-dashboard-accent-dark" />
							{t("contractorDetail.serviceAreas")}
						</h2>
						<div className="flex flex-wrap gap-2">
							{contractor.serviceAreas.map((area) => (
								<span
									key={area}
									className="inline-flex items-center rounded-full bg-dashboard-surface border border-dashboard-border px-3 py-1 font-body text-xs text-dashboard-text"
								>
									{area}
								</span>
							))}
						</div>
					</div>
				)}

				{/* Work details: what they take on and what it costs */}
				{hasWorkDetails && (
					<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper p-6 shadow-[var(--dashboard-shadow-1)]">
						<h2 className="font-display text-[18px] font-medium text-dashboard-text border-b border-dashboard-divider pb-2.5 mb-4 flex items-center gap-2">
							<Hammer className="h-4.5 w-4.5 text-dashboard-accent-dark" />
							{t("contractorDetail.workDetails")}
						</h2>
						<dl className="space-y-3 font-body text-sm text-dashboard-text">
							{hasPriceRange && (
								<div className="flex justify-between gap-4">
									<dt className="text-dashboard-muted">
										{t("contractorDetail.priceRange")}
									</dt>
									<dd>
										{contractor.priceRangeMin != null &&
										contractor.priceRangeMax != null
											? `${formatPrice(contractor.priceRangeMin)} – ${formatPrice(contractor.priceRangeMax)}`
											: formatPrice(
													(contractor.priceRangeMin ??
														contractor.priceRangeMax) as number,
												)}
									</dd>
								</div>
							)}
							{contractor.serviceRadiusKm != null && (
								<div className="flex justify-between gap-4">
									<dt className="text-dashboard-muted">
										{t("contractorDetail.serviceRadius")}
									</dt>
									<dd>
										{t("contractorDetail.serviceRadiusKm", {
											count: contractor.serviceRadiusKm,
										})}
									</dd>
								</div>
							)}
						</dl>
						{(projectTypes.length > 0 || contractor.projectTypesOther) && (
							<div className="mt-4 flex flex-wrap gap-2">
								{projectTypes.map((type) => (
									<span
										key={type}
										className="inline-flex items-center rounded-full bg-dashboard-surface border border-dashboard-border px-3 py-1 font-body text-xs text-dashboard-text"
									>
										{t(
											`onboardingOptions.contractorProjectTypes.${type}`,
											type,
										)}
									</span>
								))}
								{contractor.projectTypesOther && (
									<span className="inline-flex items-center rounded-full bg-dashboard-surface border border-dashboard-border px-3 py-1 font-body text-xs text-dashboard-text">
										{contractor.projectTypesOther}
									</span>
								)}
							</div>
						)}
					</div>
				)}

				{/* Company and team, once the contractor has published them */}
				{published && (
					<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper p-6 shadow-[var(--dashboard-shadow-1)]">
						<h2 className="font-display text-[18px] font-medium text-dashboard-text border-b border-dashboard-divider pb-2.5 mb-4 flex items-center gap-2">
							<Users className="h-4.5 w-4.5 text-dashboard-accent-dark" />
							{t("contractorDetail.companyAndTeam")}
						</h2>
						<p className="font-display text-[16px] font-medium text-dashboard-text">
							{published.companyName}
						</p>
						{published.companyDescription && (
							<p className="mt-1.5 font-body text-sm text-dashboard-muted-dark leading-relaxed">
								{published.companyDescription}
							</p>
						)}
						{published.teamMembers.length > 0 && (
							<>
								<h3 className="mt-5 mb-2 font-mono text-[10px] font-semibold uppercase tracking-widest text-dashboard-muted">
									{t("contractorDetail.teamMembers")}
								</h3>
								<ul className="space-y-2.5">
									{published.teamMembers.map((member) => (
										<li key={member.id} className="font-body text-sm">
											<span className="font-semibold text-dashboard-text">
												{member.name}
											</span>
											<span className="text-dashboard-muted">
												{" "}
												· {member.role}
												{member.experienceYears != null &&
													` · ${t("contractorDetail.xYears", { count: member.experienceYears })}`}
											</span>
											{member.shortBio && (
												<p className="text-dashboard-muted-dark">
													{member.shortBio}
												</p>
											)}
										</li>
									))}
								</ul>
							</>
						)}
						{published.projects.length > 0 && (
							<>
								<h3 className="mt-5 mb-2 font-mono text-[10px] font-semibold uppercase tracking-widest text-dashboard-muted">
									{t("contractorDetail.showcaseProjects")}
								</h3>
								<ul className="space-y-2">
									{published.projects.map((project) => (
										<li key={project.id} className="font-body text-sm">
											<span className="font-semibold text-dashboard-text">
												{project.title}
											</span>
											<p className="text-dashboard-muted-dark">
												{project.shortDescription}
											</p>
										</li>
									))}
								</ul>
							</>
						)}
						{published.slug && (
							<Link
								to={`/contractor-profile/${published.slug}`}
								className="mt-5 inline-block font-mono text-[10px] font-bold uppercase tracking-wider text-dashboard-accent-dark hover:underline"
							>
								{t("contractorDetail.viewFullProfile")}
							</Link>
						)}
					</div>
				)}

				{/* Previous projects */}
				{previousProjects.length > 0 && (
					<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper p-6 shadow-[var(--dashboard-shadow-1)]">
						<h2 className="font-display text-[18px] font-medium text-dashboard-text border-b border-dashboard-divider pb-2.5 mb-4 flex items-center gap-2">
							<ImageIcon className="h-4.5 w-4.5 text-dashboard-accent-dark" />
							{t("contractorDetail.previousProjects")}
						</h2>
						<ul className="space-y-3">
							{previousProjects.map((project) => (
								<li
									key={project.id ?? project.title}
									className="font-body text-sm"
								>
									<span className="font-semibold text-dashboard-text">
										{project.title}
									</span>
									<span className="text-dashboard-muted">
										{[project.location, project.year]
											.filter(Boolean)
											.map((part) => ` · ${part}`)
											.join("")}
									</span>
									{project.description && (
										<p className="text-dashboard-muted-dark">
											{project.description}
										</p>
									)}
								</li>
							))}
						</ul>
					</div>
				)}

				{/* Work Photos */}
				<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper p-6 shadow-[var(--dashboard-shadow-1)]">
					<h2 className="font-display text-[18px] font-medium text-dashboard-text border-b border-dashboard-divider pb-2.5 mb-4 flex items-center gap-2">
						<ImageIcon className="h-4.5 w-4.5 text-dashboard-accent-dark" />
						{t("contractorDetail.workPhotos")}
					</h2>
					{contractor.workPhotos && contractor.workPhotos.length > 0 ? (
						<div className="grid grid-cols-2 gap-3.5 sm:grid-cols-3">
							{contractor.workPhotos.map((imageUrl, index) => (
								<button
									type="button"
									key={imageUrl}
									aria-label={t("workPhotos.open", {
										index: index + 1,
										total: contractor.workPhotos?.length ?? 0,
									})}
									onClick={() => setLightboxIndex(index)}
									className="cursor-zoom-in overflow-hidden rounded-xl border border-dashboard-border/50 bg-dashboard-bg p-0 group/workphoto"
								>
									<img
										src={resolveMediaUrl(imageUrl)}
										alt={`${contractor.name} work`}
										className="aspect-square w-full object-cover transition-transform duration-500 group-hover/workphoto:scale-105"
									/>
								</button>
							))}
						</div>
					) : (
						<div className="border border-dashed border-dashboard-border rounded-xl p-8 text-center bg-dashboard-bg/50">
							<div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-dashboard-surface">
								<ImageIcon className="h-6 w-6 text-dashboard-accent-dark" />
							</div>
							<p className="mt-3 text-sm font-body text-dashboard-muted">
								{t("contractorDetail.noWorkPhotosYet")}
							</p>
						</div>
					)}
				</div>

				{/* Bottom CTA */}
				{canInvite && (
					<div className="rounded-2xl border border-dashboard-accent/30 bg-dashboard-paper p-6 shadow-[var(--dashboard-shadow-2)]">
						<div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
							<div>
								<p className="font-display text-lg font-medium text-dashboard-text">
									{t("contractorDetail.needForProject", {
										name: contractor.name,
									})}
								</p>
								<p className="font-body text-sm text-dashboard-muted mt-1">
									{t("contractorDetail.inviteCta")}
								</p>
							</div>
							<Button
								variant="primary"
								onClick={() => setShowInviteDialog(true)}
								className="shrink-0 h-10 rounded-xl px-5 font-mono text-[10px] font-bold uppercase tracking-wider text-dashboard-on-fill bg-dashboard-accent hover:bg-dashboard-accent-hover transition-all shadow-[var(--dashboard-shadow-1)] border-none"
							>
								<Send className="h-4 w-4" />
								{t("contractorDetail.inviteToProject")}
							</Button>
						</div>
					</div>
				)}
			</div>

			{/* Invite Dialog Overlay */}
			{contractor && (
				<ImageLightbox
					images={(contractor.workPhotos ?? []).map((src, index) => ({
						src,
						alt: t("workPhotos.alt", {
							name: contractor.name,
							index: index + 1,
						}),
					}))}
					openIndex={lightboxIndex}
					onOpenIndexChange={setLightboxIndex}
				/>
			)}

			{showInviteDialog && contractor && (
				<InviteDialog
					contractor={contractor}
					onClose={() => setShowInviteDialog(false)}
					onSuccess={handleInviteSuccess}
				/>
			)}
		</div>
	);
}
